import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Handshake, Scissors, UsersThree, Wrench } from '@phosphor-icons/react';
import { PRINCIPLES, PROJECTS, SITE } from '../data/site';
import Container from '../components/ui/Container';
import IconBadge from '../components/ui/IconBadge';
import Reveal, { staggerChild, staggerParent } from '../components/ui/Reveal';
import PageHeader from '../components/PageHeader';
import CtaBand from '../components/CtaBand';

const PRINCIPLE_ICONS = [UsersThree, Handshake, Scissors, Wrench];

const TILE_TONES = [
  { panel: 'border border-line bg-surface text-ink shadow-card', body: 'text-ink-soft', badge: 'accent' },
  { panel: 'bg-lavender-deep text-ink', body: 'text-ink-soft', badge: 'accent' },
  { panel: 'bg-night bg-night-glow text-white', body: 'text-white/75', badge: 'inverse' },
  { panel: 'bg-brand-gradient text-white', body: 'text-white/85', badge: 'inverse' }
];

const FACTS = [
  {
    label: '운영 중인 프로젝트',
    value: `${PROJECTS.length}건`,
    note: PROJECTS.map((project) => project.domain).join(', ')
  },
  {
    label: '제작 영역',
    value: '홈페이지, 관리 시스템, 포트폴리오',
    note: '목적에 따라 하나만, 또는 함께'
  },
  {
    label: '진행 방식',
    value: '한 팀이 처음부터 끝까지',
    note: '기획, 디자인, 개발, 배포'
  }
];

function AboutPage() {
  const reduce = useReducedMotion();

  return (
    <>
      <PageHeader
        title="운영하는 사람의 자리에서 만듭니다"
        description="PageN은 보기 좋은 화면을 넘어, 운영 단계에서 실제로 잘 돌아가는 웹을 만드는 작은 스튜디오입니다. 브랜드를 소개하는 기업 홈페이지, 매일 쓰는 관리자 시스템, 개인 브랜딩을 위한 포트폴리오까지 목적에 맞게 설계하고 구축합니다."
      />

      <section className="py-14 sm:py-20">
        <Container>
          <motion.dl
            className="grid gap-8 border-t border-line-strong pt-8 sm:grid-cols-3 sm:gap-6"
            variants={staggerParent(0.12, 0.1)}
            initial={reduce ? false : 'hidden'}
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
          >
            {FACTS.map((fact) => (
              <motion.div key={fact.label} variants={staggerChild(0, -36)}>
                <dt className="text-[13px] font-semibold text-ink-muted">{fact.label}</dt>
                <dd className="mt-2 text-xl font-bold tracking-tight sm:text-2xl">{fact.value}</dd>
                <dd className="mt-1 text-[14px] text-ink-muted">{fact.note}</dd>
              </motion.div>
            ))}
          </motion.dl>
        </Container>
      </section>

      <section className="pb-20 sm:pb-28">
        <Container>
          <Reveal from="left" className="max-w-2xl">
            <h2 className="text-balance text-[1.85rem] font-bold leading-[1.2] tracking-[-0.025em] sm:text-4xl lg:text-[2.75rem]">
              일하는 방식
            </h2>
            <p className="mt-4 text-base leading-[1.75] text-ink-soft sm:text-lg">
              프로젝트 크기와 상관없이 지키는 네 가지입니다.
            </p>
          </Reveal>

          <ul className="mt-12 grid gap-5 md:grid-cols-2">
            {PRINCIPLES.map((principle, index) => {
              const tone = TILE_TONES[index];
              return (
                <Reveal
                  as="li"
                  key={principle.title}
                  from={index % 2 === 0 ? 'left' : 'right'}
                  delay={index * 0.05}
                  className={`rounded-4xl p-7 sm:p-9 ${tone.panel}`}
                >
                  <IconBadge icon={PRINCIPLE_ICONS[index]} tone={tone.badge} />
                  <h3 className="mt-6 text-xl font-bold tracking-tight sm:text-2xl">{principle.title}</h3>
                  <p className={`mt-3 text-[15px] leading-[1.75] sm:text-base ${tone.body}`}>{principle.description}</p>
                </Reveal>
              );
            })}
          </ul>
        </Container>
      </section>

      <section className="pb-16 sm:pb-24">
        <Container>
          <Reveal>
            <div className="grid gap-8 rounded-4xl border border-line bg-surface p-7 shadow-card sm:p-10 lg:grid-cols-12 lg:gap-12 lg:p-12">
              <div className="lg:col-span-5">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">사업자 정보</h2>
                <p className="mt-3 text-[15px] leading-[1.75] text-ink-soft sm:text-base">
                  문의는 이메일이나 카카오톡 오픈채팅으로 받고 있습니다.
                </p>
              </div>
              <dl className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
                <div>
                  <dt className="text-[13px] font-semibold text-ink-muted">상호명</dt>
                  <dd className="mt-1 text-base font-medium">{SITE.business.company}</dd>
                </div>
                <div>
                  <dt className="text-[13px] font-semibold text-ink-muted">대표</dt>
                  <dd className="mt-1 text-base font-medium">{SITE.business.ceo}</dd>
                </div>
                <div>
                  <dt className="text-[13px] font-semibold text-ink-muted">사업자등록번호</dt>
                  <dd className="mt-1 text-base font-medium">{SITE.business.registration}</dd>
                </div>
                <div>
                  <dt className="text-[13px] font-semibold text-ink-muted">이메일</dt>
                  <dd className="mt-1">
                    <a href={`mailto:${SITE.email}`} className="text-base font-medium text-ink underline-offset-4 hover:underline">
                      {SITE.email}
                    </a>
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[13px] font-semibold text-ink-muted">카카오톡</dt>
                  <dd className="mt-1">
                    <a
                      href={SITE.kakao}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-base font-medium text-ink underline-offset-4 hover:underline"
                    >
                      오픈채팅 바로가기
                      <ArrowUpRight size={16} weight="bold" className="text-ink-faint" />
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
          </Reveal>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}

export default AboutPage;
